import { Authentication } from '@leedpulse-monorepo/classes';
import Failure from '../../../core/Failure';
import HttpClient from '../../../core/HttpClient';

// ─── Change plan for an existing number (upgrade / downgrade) ────────────────

interface ChangePlanPayload {
  /** Internal subscription document ID */
  subscription_id: string;
  /** Firestore company ID */
  company_id: string;
  /** Company API key for security validation */
  company_api_key: string;
  /** The new plan ID to switch to */
  new_plan_id: string;
}

export interface ChangePlanResponse {
  subscription_id: string;
  old_plan_id: string;
  new_plan_id: string;
  /** 'upgrade' or 'downgrade' */
  change_type: 'upgrade' | 'downgrade';
  /** Credits added (only for upgrades, null for downgrades) */
  credits_added: { sms: number; minutes: number; contacts: number } | null;
  /** ms timestamp — now for an upgrade, next billing date for a downgrade */
  effective_date: number;
}

/**
 * Change the plan of an existing number.
 * - Upgrade: immediate, prorated credits added, prorated difference invoiced by Stripe.
 * - Downgrade: scheduled for the next billing date, no proration. Plan, credits and
 *   rights stay untouched until then (cancellable via `cancelPlanChange`).
 */
export async function changePlan(
  payload: ChangePlanPayload,
  auth: Authentication,
): Promise<ChangePlanResponse> {
  try {
    const { company_api_key, ...body } = payload;
    const response = await HttpClient.post(
      '/leedflow/stripe/subscription/change-plan',
      body,
      auth.token,
      false,
      {
        'x-api-key': company_api_key,
        'x-user-uid': auth.id,
      },
    );
    return response.data;
  } catch (e) {
    console.error('changePlan error:', e);
    if (e instanceof TypeError && e.message === 'Failed to fetch') {
      throw new ChangePlanFailure('Le serveur ne répond pas');
    } else if (e instanceof Error) {
      throw new ChangePlanFailure(e.message);
    }
    return Promise.reject(e);
  }
}

// ─── Failure class ───────────────────────────────────────────────────────────

export class ChangePlanFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

